import React, { ReactElement } from 'react';

import { AfterSubmitFunc, FormRows, FormTemplate, If, Right, SimpleBlock, SubmitFormFunc } from 'ui-builder';
import { PlanTask, TeamPermission, ApiVersion } from 'nekst-api';
import Row3x3x6x from '../../../../shared/web/uitheme/form/Row3x3x6x';
import Checkbox from '../../../../shared/uibuilder/form/input/Checkbox';
import Row6x6x from '../../../../shared/web/uitheme/form/Row6x6x';
import Separator from '../../../../shared/web/uitheme/form/Separator';
import RelativeRow, { EmptyCell } from '../../../../shared/web/uitheme/form/RelativeRow';
import EmailTransactionPartiesInput from './EmailTransactionPartiesInput';
import { ValidationSchema } from '../../../../shared/uibuilder/validation';
import { useProjectTypeAndSetIdContextRequired } from '../../../view/ViewPlan';
import {
  DueDateConfigInput, EmailPlanTaskSmartTagsWysiwyg,
  EmailPlanTaskTitleInput,
  SendFromDropdown,
  TasksGroupsDropdown
} from 'features/tasks-forms';
import { PlanPermission, useAuthorizationChecker } from 'authorization-scope';
import { BusinessDaysToggleWrapper, SendDocumentsAsAttachmentsToggleWrapper } from 'feature-toggle';
import { IfWeekendDropdown } from 'features/nekst-widgets';
import DueDateRow from '../DueDateRow';
import styles from './EmailForm.module.scss';

export interface BaseEmailTaskFormProps {
  title: string,
  submitFormFunc: SubmitFormFunc<PlanTask>,
  afterSubmitFunc?: AfterSubmitFunc,
  validationSchema: ValidationSchema,
  buttons?: ReactElement,
  initialData?: PlanTask
  planId: number
}

function CheckboxesRows() {
  const { isGranted } = useAuthorizationChecker();

  if (isGranted(TeamPermission.FEATURE_BUSINESS_DAYS)) {
    return (
      <FormRows>
        <RelativeRow weights={[2.5, 2, 3, 4.5]}>
          <Checkbox source="isKeyDate" label="Key Date" />
          <Checkbox source="isPrivate" label="Private" />
          <Checkbox source="useBusinessDaysCalculation" label="Business Days" />
          <Checkbox source="isAutoSent" label="Autosend" />
        </RelativeRow>
      </FormRows>
    );
  } else {
    return (
      <FormRows>
        <Row3x3x6x>
          <Checkbox source="isKeyDate" label="Key Date" />
          <Checkbox source="isPrivate" label="Private" />
          <Checkbox source="isDuringBusinessWeek" label="Only During Business Week" />
        </Row3x3x6x>
        <Row6x6x>
          <Checkbox source="isAutoSent" label="Autosend" />
        </Row6x6x>
      </FormRows>
    );
  }
}

export default function BaseEmailTaskForm(props: BaseEmailTaskFormProps) {
  const planContext = useProjectTypeAndSetIdContextRequired();

  const { isGranted } = useAuthorizationChecker();

  return (
    <FormTemplate<PlanTask>
      submitFormFunc={props.submitFormFunc}
      afterSubmitFunc={props.afterSubmitFunc}
      validationSchema={props.validationSchema}
      title={props.title}
      buttons={props.buttons}
      initialData={{
        ...props.initialData || {},
        _version: isGranted(TeamPermission.FEATURE_BUSINESS_DAYS) ? ApiVersion.IF_WEEKEND_SHIFT : undefined
      }}
      isDisabled={!isGranted(PlanPermission.UPDATE)}
    >
      <SimpleBlock>
        <Right>
          <Checkbox
            source="addTransactionNameAtEnd"
            label="Add Transaction Name at End"
            className={styles.addNameInput}
          />
        </Right>
        <EmailPlanTaskTitleInput
          source="name"
          label="Task Name / Email Subject Line"
          planId={props.planId}
        />
      </SimpleBlock>
      <CheckboxesRows />
      <RelativeRow
        weights={[7, 1, 4]}
        rowClassName="form-row"
        isVisible={(data) => !data?.parentTaskRelation}
        useFlex
        toBottom
      >
        <DueDateConfigInput source="dueDateConfig" label="Due" />
        <EmptyCell />
        <BusinessDaysToggleWrapper>
          <IfWeekendDropdown />
        </BusinessDaysToggleWrapper>
      </RelativeRow>
      <DueDateRow />
      <Separator />
      <RelativeRow weights={[4, 6, 2]}>
        <If conditionFunc={() => isGranted(TeamPermission.ASSIGN_TASKS_TO_OTHER_ROLES)}>
          <SendFromDropdown source="sendFromRoleId" label="Send From" />
        </If>
        <TasksGroupsDropdown source="groupId" label="Group" planId={props.planId}  />
      </RelativeRow>
      <Separator />
      <EmailPlanTaskSmartTagsWysiwyg
        source="description"
        label="Email Message"
        projectType={planContext.projectType}
        planId={props.planId}
      />
      <SendDocumentsAsAttachmentsToggleWrapper>
        <Checkbox
          source="isSendDocumentsAsAttachments"
          label="Send Any Linked Documents also as Attachments"
          hint="If the attached documents exceed 10MB, they will be shared as downloadable links instead."
        />
      </SendDocumentsAsAttachmentsToggleWrapper>
      <Separator />
      <EmailTransactionPartiesInput source="transactionParties" label="Transaction Parties" planId={props.planId} />
    </FormTemplate>
  );
}
